import { unreachable } from 'module/helpers/unreachable';
import { JSONObject } from 'module/types/common';
import { Heading03 } from 'module/ui/components/styles';
import { Editor } from '@tiptap/core';
import { FormatDocumentOperationType } from 'api/semantic-formatting';
import { useFormatDocumentContentMutation } from 'queries/ai-helper';
import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { EnhanceWithNotesSection } from './sections/enhance-with-notes';
import { OutputContentSection } from './sections/output-content';
import { RequestedForContentSection } from './sections/selected-content';
import { getRequestBlockTitle } from './title-and-icon';
import { AskAiCommand, AskAiRequestPayload } from '../../commands/types';

interface Props {
  request: AskAiRequestPayload;
  parentEditor: Editor;
  renderOriginalContentWithDifference?: boolean;
  onAction: (content: JSONObject[]) => void;
  onNewRequest: (request: AskAiRequestPayload, editor: Editor) => void;
  documentId: number;
}

const RequestTitle = styled.div`
  ${Heading03};
`;
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
`;

const isEnhanceWithNotes = (request: AskAiRequestPayload) => {
  return request.command === AskAiCommand.EnhanceWithMyNotes;
};
const getOperationType = (command: AskAiCommand) => {
  switch (command) {
    case AskAiCommand.EnhanceWithMyNotes:
      return FormatDocumentOperationType.EnhanceWithMyNotes;
    case AskAiCommand.MakeLonger:
      return FormatDocumentOperationType.MakeLonger;
    case AskAiCommand.MakeShorter:
      return FormatDocumentOperationType.MakeShorter;
    default:
      return unreachable(command);
  }
};

export const AskAiRequest: FC<Props> = ({
  request,
  onAction,
  parentEditor,
  renderOriginalContentWithDifference = false,
  onNewRequest,
  documentId,
}) => {
  const {
    mutateAsync: formatDocumentContent,
    isPending: isFormatDocumentContentRequestPending,
    isIdle: isFormatDocumentContentRequestNotInProgress,
  } = useFormatDocumentContentMutation();
  const [aiProcessedContent, setAiProcessedContent] = useState(request.content);
  const [streamingInfo, setStreamingInfo] = useState<any | null>();

  const isContentReady =
    !isFormatDocumentContentRequestNotInProgress &&
    !isFormatDocumentContentRequestPending &&
    streamingInfo != null;

  useEffect(() => {
    formatDocumentContent({
      documentId,
      operationType: getOperationType(request.command),
      tiptapPayload: {
        content: request.content,
      },
      isStreaming: true,
      question: isEnhanceWithNotes(request)
        ? request.enhanceWithNotes
        : undefined,
    }).then((response) => {
      setAiProcessedContent(response.response.content);
      setStreamingInfo(response);
    });
  }, [documentId, formatDocumentContent, request]);

  return (
    <Wrapper>
      <RequestTitle>{getRequestBlockTitle(request.command)}</RequestTitle>
      <RequestedForContentSection content={request.content} />
      {isEnhanceWithNotes(request) && (
        <EnhanceWithNotesSection notes={request.enhanceWithNotes} />
      )}
      <OutputContentSection
        loading={!isContentReady}
        streamingInfo={streamingInfo}
        parentEditor={parentEditor}
        request={request}
        processedContent={aiProcessedContent}
        onNewRequest={onNewRequest}
        onAction={onAction}
        renderOriginalContentWithDifference={
          renderOriginalContentWithDifference
        }
      />
    </Wrapper>
  );
};
